import axios from '../utils/axiosConfig';
import { Newsletter, ApiResponse, ApiListResponse } from '../types/contentManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class NewsletterService {
  private baseURL = `${API_BASE_URL}/newsletters`;

  async getAllNewsletters(includeHidden: boolean = false, frequency?: string): Promise<Newsletter[]> {
    try {
      let url = this.baseURL;
      const params = new URLSearchParams();
      
      if (includeHidden) params.append('includeHidden', 'true');
      if (frequency) params.append('frequency', frequency);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get<ApiListResponse<Newsletter>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch newsletters');
    } catch (error) {
      console.error('Error fetching newsletters:', error);
      throw error;
    }
  }

  async getVisibleNewsletters(frequency?: string): Promise<Newsletter[]> {
    return this.getAllNewsletters(false, frequency);
  }

  async getNewslettersByFrequency(frequency: string, includeHidden: boolean = false): Promise<Newsletter[]> {
    return this.getAllNewsletters(includeHidden, frequency);
  }

  async getNewsletterById(id: number): Promise<Newsletter> {
    try {
      const response = await axios.get<ApiResponse<Newsletter>>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch newsletter');
    } catch (error) {
      console.error('Error fetching newsletter:', error);
      throw error;
    }
  }

  async createNewsletter(formData: FormData): Promise<Newsletter> {
    try {
      const response = await axios.post<ApiResponse<Newsletter>>(this.baseURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create newsletter');
    } catch (error) {
      console.error('Error creating newsletter:', error);
      throw error;
    }
  }

  async updateNewsletter(id: number, formData: FormData): Promise<Newsletter> {
    try {
      const response = await axios.put<ApiResponse<Newsletter>>(`${this.baseURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update newsletter');
    } catch (error) {
      console.error('Error updating newsletter:', error);
      throw error;
    }
  }

  async deleteNewsletter(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.baseURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete newsletter');
      }
    } catch (error) {
      console.error('Error deleting newsletter:', error);
      throw error;
    }
  }

  async toggleVisibility(id: number): Promise<Newsletter> {
    try {
      const response = await axios.patch<ApiResponse<Newsletter>>(`${this.baseURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  getFileUrl(filePath: string | undefined | null): string {
    if (!filePath || typeof filePath !== 'string') {
      return '';
    }
    
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    if (filePath.startsWith('/uploads/')) {
      return `${API_BASE_URL.replace('/api', '')}${filePath}`;
    }
    
    return `${API_BASE_URL.replace('/api', '')}/uploads/${filePath}`;
  }

  getNewsletterIcon(hasFile: boolean): string {
    return hasFile ? '📰' : '🔗';
  }

  getFrequencyColor(frequency: string): string {
    const colors: Record<string, string> = {
      'Monthly': '#3498db',
      'Quarterly': '#e74c3c',
      'Yearly': '#2ecc71'
    };
    return colors[frequency] || '#95a5a6';
  }

  getMonthsList(): string[] {
    return [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
  }

  getQuartersList(): string[] {
    return ['Q1', 'Q2', 'Q3', 'Q4'];
  }
}

export default new NewsletterService();
